<template>
  <div
    class="main-menu menu-fixed menu-accordion menu-shadow"
    :class="[
      { 'expanded': !isVerticalMenuCollapsed || (isVerticalMenuCollapsed && isMouseHovered) },
      skin === 'semi-dark' ? 'menu-dark' : 'menu-light'
    ]"
    @mouseenter="updateMouseHovered(true)"
    @mouseleave="updateMouseHovered(false)"
  >
    <!-- main menu header-->
    <div class="navbar-header expanded">
      <slot
        name="header"
        :toggleVerticalMenuActive="toggleVerticalMenuActive"
        :toggleCollapsed="toggleCollapsed"
        :collapseTogglerIcon="collapseTogglerIcon"
      >
        <ul class="nav navbar-nav flex-row">

          <!-- Logo & Text -->
          <li class="nav-item mr-auto d-flex align-items-center">
            <span class="navbar-brand">
              <span class="brand-logo">
                <b-img
                  :src="companyLogo"
                  alt="brandlogo"
                />
              </span>
            </span>
          </li>

          <!-- Toggler Button -->
          <li class="nav-item nav-toggle">
            <button
              type="button"
              class="close modern-nav-close d-xl-none"
              aria-label="Close"
              @click="toggleVerticalMenuActive"
            >
              <span aria-hidden="true">&times;</span>
            </button>
            <b-link class="nav-link modern-nav-toggle d-none d-xl-block">
              <feather-icon
                :icon="collapseTogglerIconFeather"
                size="20"
                class="collapse-toggle-icon"
                @click="toggleCollapsed"
              />
            </b-link>
          </li>
        </ul>
      </slot>
    </div>
    <!-- / main menu header-->

    <!-- Shadow -->
    <div
      :class="{'d-block': shallShadowBottom}"
      class="shadow-bottom"
    />

    <!-- main menu content-->
    <vue-perfect-scrollbar
      :settings="perfectScrollbarSettings"
      class="main-menu-content scroll-area"
      tagname="ul"
      @ps-scroll-y="evt => { shallShadowBottom = evt.srcElement.scrollTop > 0 }"
    >
      <vertical-nav-menu-items
        :items="navMenuItems"
        class="navigation navigation-main"
      />
    </vue-perfect-scrollbar>
    <!-- /main menu content-->
  </div>
</template>

<script>
import staticNavMenuItems from '@/navigation/vertical'
import VuePerfectScrollbar from 'vue-perfect-scrollbar'
import { BLink, BImg } from 'bootstrap-vue'
import { provide, computed, ref } from 'vue'
import { useStore } from 'vuex'
import useAppConfig from '@core/app-config/useAppConfig'
import { $themeConfig } from '@themeConfig'
import VerticalNavMenuItems from './components/vertical-nav-menu-items/VerticalNavMenuItems.vue'
import useVerticalNavMenu from './useVerticalNavMenu'
import { companyBranding } from '@/libs/companyGlobals'

export default {
  components: {
    VuePerfectScrollbar,
    VerticalNavMenuItems,
    BLink,
    BImg,
  },
  props: {
    isVerticalMenuActive: {
      type: Boolean,
      required: true,
    },
    toggleVerticalMenuActive: {
      type: Function,
      required: true,
    },
  },
  computed: {
    companyLogo() {
      return companyBranding.logoUrl
    },
  },
  setup(props) {
    const store = useStore()
    const {
      isMouseHovered,
      isVerticalMenuCollapsed,
      collapseTogglerIcon,
      toggleCollapsed,
      updateMouseHovered,
    } = useVerticalNavMenu(props)

    const { skin } = useAppConfig()

    // Shadow bottom is UI specific and can be removed by user => It's not in `useVerticalNavMenu`
    const shallShadowBottom = ref(false)

    provide('isMouseHovered', isMouseHovered)

    const perfectScrollbarSettings = {
      maxScrollbarLength: 60,
      wheelPropagation: false,
    }

    const collapseTogglerIconFeather = computed(() => (collapseTogglerIcon.value === 'unpinned' ? 'CircleIcon' : 'DiscIcon'))

    const navMenuItems = computed(() => {
      const items = [...staticNavMenuItems]
      const moduleGroups = store.getters['modulesRegistry/navGroups'] || []
      if (!moduleGroups.length) {
        return items
      }

      const profileIdx = items.findIndex((item) => item.route === 'profile')
      if (profileIdx >= 0) {
        items.splice(profileIdx, 0, ...moduleGroups)
      } else {
        items.push(...moduleGroups)
      }

      return items
    })

    // App Name
    const { appName, appLogoImage } = $themeConfig.app

    return {
      navMenuItems,
      perfectScrollbarSettings,
      isVerticalMenuCollapsed,
      collapseTogglerIcon,
      toggleCollapsed,
      isMouseHovered,
      updateMouseHovered,
      collapseTogglerIconFeather,

      // Shadow Bottom
      shallShadowBottom,

      // Skin
      skin,

      // App Name
      appName,
      appLogoImage,
    }
  },
}
</script>

<style lang="scss">
@import "~@core/scss/base/core/menu/menu-types/vertical-menu.scss";

.main-menu {
  .navbar-header {
    position: relative;
    width: 100%;

    .nav.navbar-nav {
      position: static;
      width: 100%;
      justify-content: center;
      align-items: center;
    }

    .nav-item.mr-auto {
      margin-right: 0 !important;
      flex: 1 1 auto;
      justify-content: center;
    }

    .navbar-brand {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 100%;
      margin-left: 0;
      margin-right: 0;
      padding-left: 0;
      padding-right: 0;
    }

    .brand-logo {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 100%;

      img {
        display: block;
        margin-left: auto;
        margin-right: auto;
      }
    }

    .nav-item.nav-toggle {
      position: absolute;
      top: 0.35rem;
      right: 0.5rem;
      left: auto;
      bottom: auto;
      z-index: 5;
      transform: none;
      margin: 0;

      .modern-nav-close.close {
        float: none;
        padding: 0.3rem 0.75rem;
        margin: 0;
        font-size: 1.85rem;
        font-weight: 700;
        line-height: 1;
        color: #6e6b7b;
        text-shadow: none;
        opacity: 1;
        background: #fff;
        border: 0;
        border-radius: 0.357rem;
        box-shadow: 0 5px 20px 0 rgba(0, 0, 0, 0.18);
        transition: all 0.23s ease 0.1s;

        &:hover,
        &:focus,
        &:active {
          opacity: 1;
          outline: none;
          color: #5e5873;
          box-shadow: 0 3px 12px 0 rgba(0, 0, 0, 0.22);
        }

        span {
          line-height: 1;
        }
      }

      .modern-nav-toggle {
        padding: 0.15rem;
        line-height: 1;
      }
    }
  }
}
</style>
